import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Copy, Edit3, Send, Archive, Lock, Save, Trash2, RotateCcw } from 'lucide-react';

const API_BASE = '/api/auditoria/versoes';

const statusStyle = (s) => {
  if (s === 'Publicado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Rascunho')  return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#e2e8f0', color: '#475569' }; // Arquivado
};

const ChecklistVersoes = ({ onVoltar }) => {
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null); // ID em edição
  const [mostrarNova, setMostrarNova] = useState(false);

  const token = () => localStorage.getItem('authToken');
  const auth = () => ({ Authorization: `Bearer ${token()}` });

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(API_BASE, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar versões.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const novaVersao = async (nome) => {
    if (!nome?.trim()) return;
    const r = await fetch(API_BASE, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao criar.'); return; }
    setMostrarNova(false);
    await carregar();
    setEditando(data.id);
  };

  const duplicar = async (id, metodoOrigem) => {
    const nome = window.prompt('Nome da nova versão (cópia):');
    if (!nome?.trim()) return;
    // DEM-00011 (T15): sem escolha, a cópia HERDA o método da origem — é o caso
    // comum (duplicar só para ajustar perguntas). Só perguntamos quando a
    // origem é PESO, porque é aí que mora o caso real desta tarefa: duplicar a
    // versão publicada para nascer uma CONTAGEM, sem mexer nas perguntas nem
    // nas auditorias já gravadas na origem.
    let metodo;
    if (metodoOrigem === 'PESO') {
      metodo = window.confirm(
        'Esta versão usa o método PESO (legado).\n\n'
        + 'OK = criar a cópia em CONTAGEM PURA (régua nova Verde/Amarelo/Laranja/Vermelho, sem peso).\n'
        + 'Cancelar = manter PESO nesta cópia.'
      ) ? 'CONTAGEM' : 'PESO';
    }
    const r = await fetch(`${API_BASE}/${id}/duplicar`, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome, ...(metodo ? { metodo } : {}) }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao duplicar.'); return; }
    await carregar();
    setEditando(data.id);
  };

  const publicar = async (id) => {
    if (!window.confirm('Publicar esta versão? Após o primeiro uso ela ficará imutável.')) return;
    const r = await fetch(`${API_BASE}/${id}/publicar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao publicar.'); return; }
    carregar();
  };

  const arquivar = async (id) => {
    if (!window.confirm('Arquivar esta versão? Ela some das listas mas as auditorias já feitas continuam preservadas.')) return;
    const r = await fetch(`${API_BASE}/${id}/arquivar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao arquivar.'); return; }
    carregar();
  };

  const republicar = async (id) => {
    if (!window.confirm('Republicar esta versão? Ela volta a aparecer na lista de checklists para preenchimento.')) return;
    const r = await fetch(`${API_BASE}/${id}/republicar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao republicar.'); return; }
    carregar();
  };

  if (editando) {
    return <VersaoEditor id={editando} onSair={() => { setEditando(null); carregar(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Gerenciar Versões do Checklist</h3>
      </div>

      <button
        onClick={() => setMostrarNova(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 16px', background: '#059669', color: '#fff',
          border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16,
        }}
      >
        <Plus size={16} /> Nova versão (em branco)
      </button>

      {mostrarNova && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <input
            type="text" autoFocus placeholder="Nome da nova versão"
            onKeyDown={(e) => { if (e.key === 'Enter') novaVersao(e.target.value); if (e.key === 'Escape') setMostrarNova(false); }}
            style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            Pressione Enter para criar — a versão começa como Rascunho.
          </div>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {carregando && <p style={{ color: '#64748b' }}>Carregando…</p>}

      <div style={{ display: 'grid', gap: 10 }}>
        {versoes.map((v) => {
          const st = statusStyle(v.STATUS);
          const imutavel = v.STATUS === 'Publicado' && v.USOS > 0;
          return (
            <div key={v.ID} style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                    background: st.bg, color: st.color,
                  }}>{v.STATUS}</span>
                  {imutavel && (
                    <span title="Versão imutável: já tem auditorias" style={{ color: '#64748b' }}>
                      <Lock size={14} />
                    </span>
                  )}
                </div>
                {v.DESCRICAO && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{v.DESCRICAO}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {v.METODO === 'CONTAGEM' ? 'Contagem pura (sem peso)' : `Peso total: ${v.PESO_MAXIMO_TOTAL}`} · Usos: {v.USOS}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditando(v.ID)} title={imutavel ? 'Visualizar (imutável)' : 'Editar'}
                  style={btn('#f1f5f9', '#475569')}>
                  <Edit3 size={14} /> {imutavel ? 'Ver' : 'Editar'}
                </button>
                <button onClick={() => duplicar(v.ID, v.METODO)} title="Duplicar" style={btn('#eff6ff', '#1d4ed8')}>
                  <Copy size={14} /> Duplicar
                </button>
                {v.STATUS === 'Rascunho' && (
                  <button onClick={() => publicar(v.ID)} title="Publicar" style={btn('#d1fae5', '#065f46')}>
                    <Send size={14} /> Publicar
                  </button>
                )}
                {v.STATUS === 'Publicado' && (
                  <button onClick={() => arquivar(v.ID)} title="Arquivar" style={btn('#f1f5f9', '#475569')}>
                    <Archive size={14} /> Arquivar
                  </button>
                )}
                {v.STATUS === 'Arquivado' && (
                  <button onClick={() => republicar(v.ID)} title="Republicar" style={btn('#d1fae5', '#065f46')}>
                    <RotateCcw size={14} /> Republicar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const btn = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', background: bg, color, border: 'none',
  borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});

// ---------------------------------------------------------------------
// Editor da versão (em mesmo arquivo para reduzir fragmentação)
// ---------------------------------------------------------------------
const VersaoEditor = ({ id, onSair }) => {
  const [dados, setDados] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const token = () => localStorage.getItem('authToken');
  const auth = () => ({ Authorization: `Bearer ${token()}` });

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao carregar.');
      // Normaliza para o estado local (lowercase)
      setDados({
        nome_versao: data.versao.NOME_VERSAO,
        descricao: data.versao.DESCRICAO || '',
        metodo: data.versao.METODO || 'PESO',
        status: data.versao.STATUS,
        podeEditar: data.podeEditar,
        usos: data.usos,
        areas: (data.areas || []).map((a) => ({
          id: a.ID, nome: a.NOME, ordem: a.ORDEM,
          perguntas: (a.perguntas || []).map((p) => ({
            id: p.ID, texto: p.TEXTO, peso: p.PESO, ordem: p.ORDEM, permite_na: !!p.PERMITE_NA,
          })),
        })),
      });
    } catch (e) {
      setErro(e.message);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const ro = !dados.podeEditar;

  const atualizarArea = (i, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[i] = { ...novas[i], [campo]: valor };
      return { ...d, areas: novas };
    });
  };
  const adicionarArea = () => {
    setDados((d) => ({ ...d, areas: [...d.areas, { nome: 'Nova Área', ordem: d.areas.length + 1, perguntas: [] }] }));
  };
  const removerArea = (i) => {
    setDados((d) => ({ ...d, areas: d.areas.filter((_, idx) => idx !== i) }));
  };
  const adicionarPergunta = (i) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[i] = {
        ...novas[i],
        perguntas: [...novas[i].perguntas, { texto: '', peso: 5, ordem: novas[i].perguntas.length + 1 }],
      };
      return { ...d, areas: novas };
    });
  };
  const atualizarPergunta = (ai, pi, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      const ps = [...novas[ai].perguntas];
      ps[pi] = { ...ps[pi], [campo]: campo === 'peso' ? Number(valor) || 0 : valor };
      novas[ai] = { ...novas[ai], perguntas: ps };
      return { ...d, areas: novas };
    });
  };
  const removerPergunta = (ai, pi) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[ai] = { ...novas[ai], perguntas: novas[ai].perguntas.filter((_, i) => i !== pi) };
      return { ...d, areas: novas };
    });
  };

  const salvar = async () => {
    setSalvando(true); setErro(''); setSucesso('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_versao: dados.nome_versao,
          descricao: dados.descricao,
          metodo: dados.metodo,
          areas: dados.areas.map((a, ai) => ({
            nome: a.nome, ordem: ai + 1,
            perguntas: a.perguntas.map((p, pi) => ({
              texto: p.texto, peso: Number(p.peso || 0), ordem: pi + 1, permite_na: p.permite_na ? 1 : 0,
            })),
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      setSucesso('Salvo. Pesos recalculados.');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const pesoTotal = dados.areas.reduce(
    (acc, a) => acc + a.perguntas.reduce((s, p) => s + Number(p.peso || 0), 0), 0
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onSair} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Editor: {dados.nome_versao}
        </h3>
      </div>

      {ro && (
        <div style={{
          padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 8, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Lock size={16} /> Versão imutável: já tem {dados.usos} auditoria(s) gravada(s). Para modificar, use "Duplicar".
        </div>
      )}
      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {sucesso && <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12 }}>{sucesso}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
            <input type="text" disabled={ro} value={dados.nome_versao}
              onChange={(e) => setDados({ ...dados, nome_versao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Descrição</label>
            <input type="text" disabled={ro} value={dados.descricao}
              onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            {/* DEM-00011 (T15): o método viaja na VERSÃO — só é trocável aqui
                porque `ro` (podeEditar) já garante ZERO auditorias usando esta
                versão. Trocar depois de usada mudaria o sentido de auditorias
                já gravadas. */}
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Método da nota</label>
            <select disabled={ro} value={dados.metodo}
              onChange={(e) => setDados({ ...dados, metodo: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
              <option value="PESO">Peso (legado)</option>
              <option value="CONTAGEM">Contagem pura (régua nova)</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
          Status: <strong>{dados.status}</strong> ·{' '}
          {dados.metodo === 'CONTAGEM'
            ? <>Método: <strong>Contagem pura</strong></>
            : <>Peso total: <strong>{pesoTotal}</strong></>}
          {' '}· Áreas: <strong>{dados.areas.length}</strong>
        </div>
      </div>

      {dados.areas.map((a, ai) => (
        <div key={ai} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input type="text" disabled={ro} value={a.nome}
              onChange={(e) => atualizarArea(ai, 'nome', e.target.value)}
              style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 700 }} />
            {!ro && (
              <button onClick={() => removerArea(ai)} style={btn('#fee2e2', '#991b1b')} title="Remover área">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {a.perguntas.map((p, pi) => (
              <div key={pi} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" disabled={ro} value={p.texto} placeholder="Texto da pergunta"
                  onChange={(e) => atualizarPergunta(ai, pi, 'texto', e.target.value)}
                  style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                <input type="number" disabled={ro} value={p.peso} min={0}
                  onChange={(e) => atualizarPergunta(ai, pi, 'peso', e.target.value)}
                  style={{ width: 80, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                {/* Marca se a pergunta pode ser declarada Não Aplicável no preenchimento
                    (ex.: itens de Delivery/Quiosque em loja que não tem). */}
                <label title="Permite marcar esta pergunta como Não Aplicável na auditoria"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" disabled={ro} checked={!!p.permite_na}
                    onChange={(e) => atualizarPergunta(ai, pi, 'permite_na', e.target.checked)} />
                  N/A
                </label>
                {!ro && (
                  <button onClick={() => removerPergunta(ai, pi)} style={btn('#fee2e2', '#991b1b')} title="Remover">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            {!ro && (
              <button onClick={() => adicionarPergunta(ai)} style={btn('#eff6ff', '#1d4ed8')}>
                <Plus size={12} /> Pergunta
              </button>
            )}
          </div>
        </div>
      ))}

      {!ro && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={adicionarArea} style={{
            padding: '10px 16px', background: '#f1f5f9', color: '#475569',
            border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <Plus size={14} /> Área
          </button>
          <button onClick={salvar} disabled={salvando} style={{
            padding: '10px 20px', background: salvando ? '#94a3b8' : '#059669', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={14} /> {salvando ? 'Salvando…' : 'Salvar estrutura'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChecklistVersoes;
